import React, { useState, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputLabel,
  Typography,
  Box,
} from "@mui/material";
import {
  setResetPasswordDialogOpen,
  resetErrors,
} from "../../../../redux/slices/profileSlice";
import { resetPasswordAuthorized } from "../../../../redux/slices/projectsSlice";
import PasswordField from "../../../../components/PasswordField";

const ResetPasswordDialog = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector((state) => state.login);
  const {
    resetPasswordDialogOpen,
    passwordResetting,
    passwordError,
    passwordResetSuccess,
  } = useAppSelector((state) => state.profile);

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});

  const handleClose = useCallback(() => {
    dispatch(setResetPasswordDialogOpen(false));
    dispatch(resetErrors());
    setFormData({
      newPassword: "",
      confirmPassword: "",
    });
    setValidationErrors({});
  }, [dispatch]);

  const handleChange = useCallback(
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear validation errors when user starts typing
      if (validationErrors[field]) {
        setValidationErrors((prev) => ({ ...prev, [field]: null }));
      }
      if (passwordError) {
        dispatch(resetErrors());
      }
    },
    [validationErrors, passwordError, dispatch]
  );

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!formData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (!user?.id) {
        enqueueSnackbar("User ID not found", { variant: "error" });
        return;
      }

      dispatch(
        resetPasswordAuthorized({
          newPassword: formData.newPassword,
        })
      );
    },
    [dispatch, formData, validateForm, user, enqueueSnackbar]
  );

  // Close dialog on successful password reset
  React.useEffect(() => {
    if (passwordResetSuccess) {
      handleClose();
      enqueueSnackbar("Password reset successfully!", { variant: "success" });
    }
  }, [passwordResetSuccess, handleClose, enqueueSnackbar]);

  return (
    <Dialog
      open={resetPasswordDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 0 },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose a new password for your account
        </Typography>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* New Password */}
            <Box>
              <InputLabel htmlFor="new-password" sx={{ fontSize: 14, mb: 0.5 }}>
                New Password
              </InputLabel>
              <PasswordField
                id="new-password"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Enter your new password"
                value={formData.newPassword}
                onChange={handleChange("newPassword")}
                disabled={passwordResetting}
                error={!!validationErrors.newPassword}
                helperText={validationErrors.newPassword}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 0 },
                  "& .MuiOutlinedInput-input": { p: "8.5px 14px" },
                }}
              />
            </Box>

            {/* Confirm Password */}
            <Box>
              <InputLabel
                htmlFor="confirm-password"
                sx={{ fontSize: 14, mb: 0.5 }}
              >
                Confirm New Password
              </InputLabel>
              <PasswordField
                id="confirm-password"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={handleChange("confirmPassword")}
                disabled={passwordResetting}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 0 },
                  "& .MuiOutlinedInput-input": { p: "8.5px 14px" },
                }}
              />
            </Box>

            {/* Server Error */}
            {passwordError && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  fontSize: 12,
                  textAlign: "center",
                  mt: 1,
                }}
              >
                {passwordError}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={passwordResetting}
            sx={{
              borderRadius: 0,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={passwordResetting}
            sx={{
              borderRadius: 0,
              px: 3,
            }}
          >
            {passwordResetting ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ResetPasswordDialog;
